import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import Modal from 'components/molecules/Modal'
import { FactsFields, Field, Notice, SimulationResult, btn, inputClass, omr, toFactsPayload, useRefundSettings, validateFacts } from 'pages/policy/refundShared'

/**
 * Admin cancel with cancelPaymentType 'policy': the refund engine picks the rule.
 * Confirm is only enabled after a preview (refunds/simulate) of the exact same inputs.
 */
const PolicyCancelModal = ({ bookingId, sendCancellationNotification, onClose, onCancelled }) => {
  const { t } = useTranslation()
  const [settings] = useRefundSettings()
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)

  const setValue = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }))
    setErrors(prev => ({ ...prev, [key]: undefined }))
    setPreview(null)
  }

  const validate = () => {
    const nextErrors = validateFacts(values, t)
    setErrors(nextErrors)
    return isEmpty(nextErrors)
  }

  const runPreview = async () => {
    if (!validate()) return
    setLoading(true)
    const { notes, ...facts } = values
    const response = await apiPost('refunds/simulate', { ...toFactsPayload(facts), bookingId })
    setLoading(false)
    setPreview(response?.success ? response.data : null)
  }

  const confirmCancel = async () => {
    if (!validate()) return
    setLoading(true)
    const response = await apiPost(`booking/cancel/${bookingId}`, { ...toFactsPayload(values), cancelPaymentType: 'policy', sendCancellationNotification })
    setLoading(false)
    if (response?.success) {
      const { amount, requiresApproval, ruleCode } = response.data || {}
      notify.success(t(requiresApproval ? 'refundEngine.booking.cancelledPendingApproval' : 'refundEngine.booking.cancelled', { amount: omr(amount), rule: ruleCode || '-' }))
      onCancelled()
    }
  }

  return (
    <Modal
      open
      closeable
      onClose={onClose}
      title={t('refundEngine.booking.policyCancelTitle')}
      dimensions={{ width: '94vw', maxWidth: '960px', height: 'auto', maxHeight: '88vh' }}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-4">
            <FactsFields settings={settings} values={values} errors={errors} onChange={setValue} />
            <Field label={t('refundEngine.facts.notes')} htmlFor="policy-cancel-notes">
              <textarea id="policy-cancel-notes" rows={2} value={values.notes || ''} onChange={e => setValue('notes', e.target.value)} className={inputClass()} />
            </Field>
          </div>
          <div>{preview ? <SimulationResult result={preview} /> : <Notice type="info" title={t('refundEngine.booking.previewFirst')} />}</div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" className={btn('secondary')} onClick={runPreview} disabled={loading}>
            {t('refundEngine.booking.preview')}
          </button>
          <button type="button" className={btn('danger')} onClick={confirmCancel} disabled={!preview || loading}>
            {t('refundEngine.booking.confirmCancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PolicyCancelModal
